import {createReducer, on} from '@ngrx/store';
import {ProductManagementErrorInterface} from '../../../error/error-handling/product-management-error';
import {CollectionMetadata, getProductId, Product} from '../../../models/clare';
import {ProductListActions} from './product-list.actions';

export const productFeatureKey = 'product';

/**
 * Product list state.
 * - `items` is the products tab.
 * - `selection*` is the choose-bundle-products modal (lists products only, no bundles).
 */
export interface ProductState {
  items: Product[];
  paginationMeta: CollectionMetadata;
  loading: boolean;
  success: boolean;
  error: ProductManagementErrorInterface | undefined;
  clareAccountStatus: string;
  selectionItems: Product[];
  selectionPaginationMeta: CollectionMetadata;
  selectionLoading: boolean;
}

export interface SpecAssignment {
  specId: string;
  productId: string;
}

export const initialProductState: ProductState = {
  items: [],
  paginationMeta: {result_type: 'Items::Product', pagination: {}} as CollectionMetadata,
  loading: false,
  success: false,
  error: undefined,
  clareAccountStatus: '',
  selectionItems: [],
  selectionPaginationMeta: {result_type: 'Items::Product', pagination: {}} as CollectionMetadata,
  selectionLoading: false,
};

function updateItemById(items: Product[], product: Product): Product[] {
  return items.map(item => (getProductId(item) === getProductId(product) ? product : item));
}

export const reducer = createReducer(
  initialProductState,
  on(ProductListActions.toggleProductActiveStatus, state => ({...state, loading: true, success: false})),
  on(ProductListActions.toggleProductActiveStatusSuccess, (state, action) => ({
    ...state,
    items: updateItemById(state.items, action.product),
    loading: false,
    success: true,
  })),
  on(ProductListActions.toggleProductActiveStatusFailure, (state, action) => ({
    ...state,
    loading: false,
    success: false,
    error: action.error,
  })),
  on(ProductListActions.loadProducts, state => ({...state, loading: true, success: false})),
  on(ProductListActions.loadProductsSuccess, (state, action) => ({
    ...state,
    items: [...action.products.result],
    paginationMeta: action.products.metadata,
    loading: false,
    success: true,
  })),
  on(ProductListActions.loadProductsFailure, (state, action) => ({
    ...state,
    loading: false,
    success: false,
    error: action.error,
  })),
  on(ProductListActions.setClareAccountStatus, (state, action) => ({
    ...state,
    clareAccountStatus: action.clareAccountStatus,
  })),
  on(ProductListActions.loadProductsForSelection, state => ({...state, selectionLoading: true})),
  on(ProductListActions.loadProductsForSelectionSuccess, (state, action) => ({
    ...state,
    selectionItems: [...action.products.result],
    selectionPaginationMeta: action.products.metadata,
    selectionLoading: false,
  })),
  on(ProductListActions.loadProductsForSelectionFailure, (state, action) => ({
    ...state,
    selectionLoading: false,
    error: action.error,
  }))
);

export const getProductLoadingState = (state: ProductState): boolean => state.loading;
export const getProducts = (state: ProductState): Product[] => state.items;
export const getPaginationMeta = (state: ProductState): CollectionMetadata => state.paginationMeta;
export const getError = (state: ProductState): ProductManagementErrorInterface | undefined => state.error;
export const getClareAccountStatus = (state: ProductState): string => state.clareAccountStatus;
export const getSelectionProducts = (state: ProductState): Product[] => state.selectionItems;
export const getSelectionPaginationMeta = (state: ProductState): CollectionMetadata => state.selectionPaginationMeta;
export const getSelectionLoading = (state: ProductState): boolean => state.selectionLoading;
